import type {
  ModerationStatus,
  VisibilityScope
} from '@aurora/shared-generated/types/graphql-schema-types';

/**
 * Badge states in priority order. Higher-priority states (lower index) take precedence.
 */
export enum ModerationBadgeState {
  APPEAL_APPROVED = 'APPEAL_APPROVED',
  APPEAL_SUBMITTED = 'APPEAL_SUBMITTED',
  APPEAL_REJECTED = 'APPEAL_REJECTED',
  REJECTED = 'REJECTED',
  IN_REVIEW = 'IN_REVIEW',
  AI_APPROVED = 'AI_APPROVED',
  MODERATOR_APPROVED = 'MODERATOR_APPROVED'
}

export type BadgeVariant = 'success' | 'warning' | 'danger' | 'info';

export interface ModerationBadgePayload {
  reviewedAt?: string | null;
  reviewNotes?: string | null;
  outcome?: string | null;
  createdAt?: string | null;
  appealReason?: string | null;
  category?: string | null;
  rejectReason?: string | null;
  rejectActorType?: string | null;
  rejectTime?: string | null;
  reasonCodeDetails?: Array<{
    code: string;
    displayName?: string | null;
    description?: string | null;
    userMessage?: string | null;
  }>;
  flaggedComponent?: {
    type?: string | null;
    index?: number | null;
    verdict?: string | null;
    confidence?: number | null;
    reasoning?: string | null;
  } | null;
  aiProcessingStatus?: string | null;
  verdict?: string | null;
  actionTaken?: boolean | null;
  confidence?: number | null;
  processedAt?: string | null;
  approvalTime?: string | null;
}

export interface ModerationBadgeResult {
  state: ModerationBadgeState;
  variant: BadgeVariant;
  labelKey: string;
  payload: ModerationBadgePayload;
}

/**
 * Input data shape for the badge state derivation.
 * Mirrors the subset of MessageViewFragment fields used for moderation state.
 */
interface ModerationBadgeInput {
  visibilityScope?: VisibilityScope | string | null;
  moderationData?: {
    status?: ModerationStatus | string | null;
    rejectReason?: string | null;
    rejectActorType?: string | null;
    rejectTime?: string | null;
    approveActorType?: string | null;
  } | null;
  latestAppeal?: {
    status?: string | null;
    category?: string | null;
    appealReason?: string | null;
    createdAt?: string | null;
    reviewedAt?: string | null;
    outcome?: string | null;
    reviewNotes?: string | null;
  } | null;
  aiProcessingStatus?: string | null;
  aiModerationResult?: {
    verdict?: string | null;
    actionTaken?: boolean | null;
    confidence?: number | null;
    processedAt?: string | null;
    reasonCodeDetails?: Array<{
      code: string;
      displayName?: string | null;
      description?: string | null;
      userMessage?: string | null;
    }> | null;
    flaggedComponent?: {
      type?: string | null;
      index?: number | null;
      verdict?: string | null;
      confidence?: number | null;
      reasoning?: string | null;
    } | null;
  } | null;
}

const STATE_MAP: Record<ModerationBadgeState, { variant: BadgeVariant; labelKey: string }> = {
  [ModerationBadgeState.APPEAL_APPROVED]: { variant: 'success', labelKey: 'state.appealApproved' },
  [ModerationBadgeState.APPEAL_SUBMITTED]: {
    variant: 'warning',
    labelKey: 'state.appealSubmitted'
  },
  [ModerationBadgeState.APPEAL_REJECTED]: { variant: 'danger', labelKey: 'state.appealRejected' },
  [ModerationBadgeState.REJECTED]: { variant: 'danger', labelKey: 'state.rejected' },
  [ModerationBadgeState.IN_REVIEW]: { variant: 'info', labelKey: 'state.inReview' },
  [ModerationBadgeState.AI_APPROVED]: { variant: 'success', labelKey: 'state.aiApproved' },
  [ModerationBadgeState.MODERATOR_APPROVED]: {
    variant: 'success',
    labelKey: 'state.moderatorApproved'
  }
};

/**
 * Pure derivation hook — no useState, no useEffect, no API calls.
 * Takes message data (initial or polled) and returns derived badge state.
 *
 * Returns null when no badge should show (bypass auto-approve, OFF style, no moderation data).
 *
 * KCOMM-271: Persistent Moderation Status Badge
 */
export function useModerationBadgeState(
  messageData: ModerationBadgeInput | null | undefined
): ModerationBadgeResult | null {
  if (!messageData) {
    return null;
  }

  const { moderationData, latestAppeal, aiModerationResult } = messageData;
  const { visibilityScope } = messageData;
  const { aiProcessingStatus } = messageData;
  const approveActorType = moderationData?.approveActorType;

  // 1. Appeal states (most specific — override all other states)
  if (latestAppeal?.status === 'APPROVED') {
    return {
      state: ModerationBadgeState.APPEAL_APPROVED,
      ...STATE_MAP[ModerationBadgeState.APPEAL_APPROVED],
      payload: {
        reviewedAt: latestAppeal.reviewedAt,
        reviewNotes: latestAppeal.reviewNotes,
        outcome: latestAppeal.outcome
      }
    };
  }

  if (latestAppeal?.status === 'PENDING') {
    return {
      state: ModerationBadgeState.APPEAL_SUBMITTED,
      ...STATE_MAP[ModerationBadgeState.APPEAL_SUBMITTED],
      payload: {
        createdAt: latestAppeal.createdAt,
        appealReason: latestAppeal.appealReason,
        category: latestAppeal.category
      }
    };
  }

  if (latestAppeal?.status === 'REJECTED') {
    return {
      state: ModerationBadgeState.APPEAL_REJECTED,
      ...STATE_MAP[ModerationBadgeState.APPEAL_REJECTED],
      payload: {
        reviewedAt: latestAppeal.reviewedAt,
        reviewNotes: latestAppeal.reviewNotes,
        outcome: latestAppeal.outcome
      }
    };
  }

  // 2. Rejected (explicit negative verdict, no active appeal)
  if (moderationData?.status === 'REJECTED') {
    const violationReasonCodes = aiModerationResult?.reasonCodeDetails?.filter(
      detail => !detail.code?.toUpperCase().startsWith('HAM_')
    );

    return {
      state: ModerationBadgeState.REJECTED,
      ...STATE_MAP[ModerationBadgeState.REJECTED],
      payload: {
        rejectReason: moderationData.rejectReason,
        rejectActorType: moderationData.rejectActorType,
        rejectTime: moderationData.rejectTime,
        reasonCodeDetails: violationReasonCodes?.length > 0 ? violationReasonCodes : undefined,
        flaggedComponent: aiModerationResult?.flaggedComponent
      }
    };
  }

  // 3. In review (awaiting decision)
  // Check visibilityScope (for authors) OR moderationData.status (for moderators who may see a different scope)
  const status = moderationData?.status;
  if (
    visibilityScope === 'PRIVATE_AND_UNMODERATED' ||
    status === 'UNMODERATED' ||
    status === 'Unmoderated'
  ) {
    // When AI completed with ACCEPTED verdict but low confidence (actionTaken === false),
    // include reason codes so the badge/banner can explain why the post is still in review.
    const violationReasonCodes = aiModerationResult?.reasonCodeDetails?.filter(
      detail => !detail.code?.toUpperCase().startsWith('HAM_')
    );

    return {
      state: ModerationBadgeState.IN_REVIEW,
      ...STATE_MAP[ModerationBadgeState.IN_REVIEW],
      payload: {
        aiProcessingStatus,
        verdict: aiModerationResult?.verdict,
        actionTaken: aiModerationResult?.actionTaken,
        reasonCodeDetails: violationReasonCodes?.length > 0 ? violationReasonCodes : undefined
      }
    };
  }

  // 4. Approval variants (explicit positive verdict)
  if (approveActorType?.toUpperCase() === 'SYSTEM') {
    return {
      state: ModerationBadgeState.AI_APPROVED,
      ...STATE_MAP[ModerationBadgeState.AI_APPROVED],
      payload: {
        confidence: aiModerationResult?.confidence,
        processedAt: aiModerationResult?.processedAt
      }
    };
  }

  if (approveActorType?.toUpperCase() === 'MEMBER') {
    return {
      state: ModerationBadgeState.MODERATOR_APPROVED,
      ...STATE_MAP[ModerationBadgeState.MODERATOR_APPROVED],
      payload: {
        approvalTime: moderationData?.rejectTime // reuse rejectTime field pattern for timing
      }
    };
  }

  // 5. Fallback: Approved status without approveActorType (e.g., reply data missing actor info)
  // Infer approval type from aiModerationResult — if AI data exists, it was AI approved
  if (status === 'APPROVED' || status === 'Approved') {
    if (aiModerationResult?.confidence != null || aiModerationResult?.processedAt) {
      return {
        state: ModerationBadgeState.AI_APPROVED,
        ...STATE_MAP[ModerationBadgeState.AI_APPROVED],
        payload: {
          confidence: aiModerationResult.confidence,
          processedAt: aiModerationResult.processedAt
        }
      };
    }
    return {
      state: ModerationBadgeState.MODERATOR_APPROVED,
      ...STATE_MAP[ModerationBadgeState.MODERATOR_APPROVED],
      payload: {
        approvalTime: null
      }
    };
  }

  // No badge: bypass auto-approve, OFF style, or no moderation data
  return null;
}

export default useModerationBadgeState;
